import { emit } from './bridge';

/**
 * Game-flow postMessage helpers — one place that knows the exact payload
 * shape for every buy/reveal event. Call sites (useGamePurchase,
 * RevealScreen, PackReveal) call these instead of `emit('…', { … })`
 * directly, so:
 *
 *   • Renaming an event or changing a payload field happens in one file.
 *   • Autocomplete on `gameEvents.` surfaces every available event.
 *   • A payload typo is impossible — TypeScript validates each helper's
 *     argument list against the canonical `EventPayloadMap` in events.ts.
 *
 * Session-level events (session_ready, session_expired, balance_updated,
 * error) are still emitted directly via `emit()` from their modules —
 * those aren't part of the game flow and don't share state with it.
 */
export const gameEvents = {
  purchaseInitiated(orderId: string): void {
    emit('purchase_initiated', { order_id: orderId });
  },

  purchaseCompleted(
    orderId: string,
    items: Array<{ id: string; name: string; rarity: string }>,
  ): void {
    emit('purchase_completed', { order_id: orderId, items });
  },

  /** `orderId` is null when the create-order step itself failed (we
   *  don't yet have an id to report). Falls through to the legacy
   *  literal `'unknown'` to keep the payload shape stable for parents.
   *  `code` is the backend error code (e.g. INSUFFICIENT_BALANCE) when one
   *  is known — parents branch on it (open their cashier); omitted otherwise
   *  so the legacy payload stays byte-identical. */
  purchaseFailed(orderId: string | null, message: string, code?: string): void {
    emit('purchase_failed', {
      order_id: orderId ?? 'unknown',
      message,
      ...(code !== undefined ? { code } : {}),
    });
  },

  revealStarted(orderId: string, itemCount: number): void {
    emit('reveal_started', { order_id: orderId, item_count: itemCount });
  },

  itemRevealed(
    orderId: string,
    itemId: string,
    name: string,
    fairMarketValue: number | null,
  ): void {
    emit('item_revealed', {
      order_id: orderId,
      item_id: itemId,
      name,
      fair_market_value: fairMarketValue,
    });
  },

  itemSold(orderId: string, itemId: string, grossPrice: number): void {
    emit('item_sold', {
      order_id: orderId,
      item_id: itemId,
      gross_price: grossPrice,
    });
  },

  revealComplete(orderId: string): void {
    emit('reveal_complete', { order_id: orderId });
  },
};
